import type { InvestorEngagementSummary } from '@fruuit/shared'
import {
  Button,
  Callout,
  type Column,
  DataTable,
  EmptyState,
  PageHeader,
  StatusBadge,
} from '@fruuit/ui'
import { Handshake, Info } from 'lucide-react'
import { Link } from 'react-router'
import {
  EngagementFilters,
  useEngagementFilters,
} from '../../features/engagements/EngagementFilters'
import { plural, shortDate, themeLabel } from '../../features/engagements/format'
import { useInvestorEngagements } from '../../features/engagements/queries'
import { SavedViews } from '../../features/engagements/SavedViews'
import { ProgressBar } from '../../features/engagements/StageTrack'
import { useCursorPager } from '../../features/engagements/useCursorPager'
import { useTitle } from '../../lib/useTitle'

const columns: Column<InvestorEngagementSummary>[] = [
  {
    id: 'company',
    header: 'Company / theme',
    cell: (row) => (
      <div className="min-w-0">
        <Link
          to={`/app/companies/${row.issuer.id}`}
          className="font-semibold text-ink underline-offset-4 hover:text-teal hover:underline"
        >
          {row.issuer.name}
        </Link>
        <p className="mt-0.5 text-xs text-sage">
          {themeLabel(row.theme)} · {row.manager_name}
        </p>
      </div>
    ),
  },
  {
    id: 'case',
    header: 'Engagement objective',
    cell: (row) => (
      <div className="min-w-0 max-w-sm">
        <Link
          to={`/app/engagements/${row.id}`}
          className="font-medium text-teal underline-offset-4 hover:underline"
        >
          {row.title}
        </Link>
        <p className="mt-0.5 text-xs text-sage">
          {plural(row.progress.objectives, 'objective')} · Started {shortDate(row.original_start)}
          {row.client_specific ? ' · Your organisation only' : ''}
        </p>
      </div>
    ),
  },
  {
    id: 'progress',
    header: 'Evidence progress',
    cell: (row) => (
      <div className="min-w-36">
        <ProgressBar percent={row.progress.percent} label={`Evidence progress for ${row.title}`} />
        <p className="mt-1.5 text-xs text-sage">
          {row.progress.evidenced}/{row.progress.objectives} with implementation evidence
        </p>
      </div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    id: 'next',
    header: 'Next action',
    cell: (row) => (
      <div className="min-w-0 max-w-xs">
        <p className="text-sm text-ink">{row.next_action ?? 'No next action released'}</p>
        <p className="mt-0.5 text-xs text-sage">Due {shortDate(row.due_date)}</p>
      </div>
    ),
  },
]

/** S3 Engagements (investor): the subscribed programme with filters and saved views. */
export function EngagementsPage() {
  useTitle('Engagements')
  const { values, update } = useEngagementFilters()
  const pager = useCursorPager(JSON.stringify(values))
  const list = useInvestorEngagements({
    ...values,
    ...(pager.cursor ? { cursor: pager.cursor } : {}),
  })
  const rows = list.data?.items ?? []
  const filtered = Object.values(values).some(Boolean)

  return (
    <>
      <PageHeader
        eyebrow="Engagement programme"
        title="A clear plan for every company."
        description="Company objectives, reviewed progress and the next evidence test."
      />
      <section className="rounded-lg border border-line bg-surface p-4 shadow-[0_1px_2px_rgb(22_62_58/0.04)] sm:p-6">
        <div className="mb-5 flex flex-col gap-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className="text-lg font-semibold text-ink">
              {list.isPending
                ? 'Engagement cases'
                : `${plural(rows.length, 'engagement case')}${list.data?.next_cursor || pager.hasPrevious ? ` on page ${pager.page}` : ''}`}
            </h2>
            <SavedViews current={values} onApply={(next) => update(next)} />
          </div>
          <EngagementFilters values={values} onChange={update} />
        </div>
        {list.isError ? (
          <Callout tone="danger" role="alert">
            {list.error.message}
          </Callout>
        ) : (
          <>
            {rows.length > 0 ? (
              <ul className="space-y-3 sm:hidden" aria-label="Engagement cases">
                {rows.map((row) => (
                  <li key={row.id} className="rounded-lg border border-line bg-surface p-4">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-semibold text-sage">{row.issuer.name}</p>
                      <StatusBadge status={row.status} />
                    </div>
                    <Link
                      to={`/app/engagements/${row.id}`}
                      className="mt-1 block font-semibold text-teal underline-offset-4 hover:underline"
                    >
                      {row.title}
                    </Link>
                    <p className="mt-0.5 text-xs text-sage">
                      {themeLabel(row.theme)} · {plural(row.progress.objectives, 'objective')}
                    </p>
                    <div className="mt-3">
                      <ProgressBar
                        percent={row.progress.percent}
                        label={`Evidence progress for ${row.title}`}
                      />
                    </div>
                    <p className="mt-3 text-sm text-ink">
                      {row.next_action ?? 'No next action released'}
                    </p>
                    <p className="text-xs text-sage">Due {shortDate(row.due_date)}</p>
                  </li>
                ))}
              </ul>
            ) : null}
            <DataTable
              className={rows.length > 0 ? 'hidden sm:block' : undefined}
              caption="Engagement cases"
              columns={columns}
              rows={rows}
              getRowId={(row) => row.id}
              loading={list.isPending}
              empty={
                <EmptyState
                  variant="plain"
                  icon={<Handshake />}
                  title={filtered ? 'No engagements match these filters.' : 'No engagements yet.'}
                  description={
                    filtered
                      ? 'Reset or broaden your selection.'
                      : 'Cases appear here once your organisation is subscribed and Fruuit releases them.'
                  }
                  action={
                    filtered ? (
                      <Button variant="secondary" onClick={() => update({})}>
                        Reset filters
                      </Button>
                    ) : undefined
                  }
                />
              }
              {...(list.data?.next_cursor || pager.hasPrevious
                ? {
                    pagination: {
                      mode: 'server' as const,
                      hasNext: Boolean(list.data?.next_cursor),
                      hasPrevious: pager.hasPrevious,
                      onNext: () => pager.next(list.data?.next_cursor),
                      onPrevious: pager.previous,
                      summary: `Page ${pager.page}`,
                    },
                  }
                : {})}
            />
          </>
        )}
      </section>
      <p className="mt-5 flex gap-2 text-[13px] leading-relaxed text-sage">
        <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-teal-500" />
        Objective progress and case status answer different questions. A company may respond while
        independent evidence remains outstanding.
      </p>
    </>
  )
}
